import React, { useEffect, useRef, useState } from 'react';
import { ConvertedAiTask } from '../types/ai-task';
import { AiTask } from './tasks/ai-task';

// CSS for gap filling pseudo-element
const stackItemStyles = `
  .stack-item-expanded::after {
    content: '';
    position: absolute;
    left: 0;
    height: calc(var(--gap) + 1px);
    bottom: 100%;
    width: 100%;
    z-index: -1;
  }
`;

// Types
interface HeightT {
    height: number;
    taskId: string;
}

interface StackItemProps {
    item: ConvertedAiTask;
    index: number;
    isVisible: boolean;
    isFront: boolean;
    heights: HeightT[];
    setHeights: React.Dispatch<React.SetStateAction<HeightT[]>>;
    expanded: boolean;
    setInteracting: (interacting: boolean) => void;
    totalItems: number;
    gap: number;
    cornerOffset: number;
    frontHeight: number;
}

// Constants
const VISIBLE_ITEMS_AMOUNT = 3;
const GAP = 8;
const CORNER_OFFSET = 8;
const STACK_OFFSET = 4;

// Utility function for class names
function cn(...classes: (string | undefined)[]) {
    return classes.filter(Boolean).join(' ');
}

// Individual stack item component
const StackItem: React.FC<StackItemProps> = ({
    item,
    index,
    isVisible,
    isFront,
    heights,
    setHeights,
    expanded,
    setInteracting,
    totalItems,
    gap,
    cornerOffset,
    frontHeight,
}) => {
    const [mounted, setMounted] = useState(false);
    const [initialHeight, setInitialHeight] = useState(0);
    const itemRef = useRef<HTMLDivElement>(null);

    const taskId = item.task_id || `item-${index}`;

    // Height index for calculating offset - fix the -1 issue
    const heightIndex = React.useMemo(() => {
        const foundIndex = heights.findIndex((height) => height.taskId === taskId);
        return foundIndex !== -1 ? foundIndex : index;
    }, [heights, taskId, index]);

    // Calculate stacked heights before this item (only for expanded state)
    const stackedHeightBefore = React.useMemo(() => {
        if (!expanded) return 0;

        return heights.reduce((prev, curr, reducerIndex) => {
            // Calculate offset up until current toast
            if (reducerIndex >= heightIndex) {
                return prev;
            }
            return prev + curr.height;
        }, 0);
    }, [heights, heightIndex, expanded]);

    // Calculate offset - simplified logic
    const offset = React.useMemo(() => {
        if (expanded) {
            return heightIndex * gap + stackedHeightBefore;
        } else {
            return 0; // Always start from 0 when collapsed
        }
    }, [expanded, heightIndex, gap, stackedHeightBefore]);

    // Calculate corner positions for collapsed state (bottom-right stacking)
    const cornerPositionX = React.useMemo(() => {
        // Items behind are further to the right
        return index * STACK_OFFSET;
    }, [index]);
    const cornerPositionY = React.useMemo(() => {
        // Items behind are further down
        return index * STACK_OFFSET;
    }, [index]);

    // Base corner position of the first (front) card in collapsed state (bottom-right scheme)
    const firstCornerX = 0;
    const firstCornerY = 0;

    // Mount animation
    useEffect(() => {
        setMounted(true);
    }, []);

    // Height measurement and tracking - more stable
    useEffect(() => {
        const itemNode = itemRef.current;
        if (itemNode) {
            const measured = itemNode.getBoundingClientRect().height;
            // When collapsed and not front, use frontHeight if available; otherwise wait
            const heightToStore = !expanded && !isFront
                ? (frontHeight > 0 ? frontHeight : null)
                : measured;

            if (heightToStore == null) {
                return;
            }

            setInitialHeight(heightToStore);

            setHeights((prevHeights) => {
                const existing = prevHeights.find((h) => h.taskId === taskId);
                if (existing) {
                    return prevHeights.map((h) =>
                        h.taskId === taskId ? { ...h, height: heightToStore } : h
                    );
                } else {
                    return [...prevHeights, { taskId, height: heightToStore }];
                }
            });

            return () => setHeights((h) => h.filter((height) => height.taskId !== taskId));
        }
    }, [setHeights, taskId, expanded, isFront, frontHeight]);

    // Update height when content changes - debounced
    React.useLayoutEffect(() => {
        if (!mounted) return;
        const itemNode = itemRef.current;
        if (!itemNode) return;

        const measured = itemNode.getBoundingClientRect().height;
        const nextHeight = !expanded && !isFront
            ? (frontHeight > 0 ? frontHeight : measured)
            : measured;

        if (Math.abs(nextHeight - initialHeight) > 1) { // Only update if significant change
            setInitialHeight(nextHeight);
            setHeights((heights) => {
                return heights.map((h) =>
                    h.taskId === taskId ? { ...h, height: nextHeight } : h
                );
            });
        }
    }, [mounted, item, setHeights, taskId, initialHeight, expanded, isFront, frontHeight]);

    return (
        <div
            ref={itemRef}
            className={cn(
                'absolute top-0 left-0 w-80 bg-card border border-border rounded-2xl shadow-lg',
                'transition-all duration-300 ease-out',
                expanded ? 'stack-item-expanded' : '',
                isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none',
                'cursor-pointer'
            )}
            style={{
                '--index': index,
                '--z-index': totalItems - index,
                '--offset': `${offset}px`,
                '--initial-height': `${initialHeight}px`,
                '--gap': `${gap}px`,
                zIndex: totalItems - index,
                transform: expanded
                    ? `translate(${cornerOffset + firstCornerX}px, ${cornerOffset + firstCornerY + offset}px)`
                    : `translate(${cornerOffset + cornerPositionX}px, ${cornerOffset + cornerPositionY}px)`,
                transformOrigin: 'top left',
                height: !isFront && !expanded && frontHeight > 0 ? `${frontHeight}px` : undefined,
                willChange: 'transform, height',
            } as React.CSSProperties}
            onMouseDown={(e) => {
                e.stopPropagation();
                setInteracting(true);
            }}
            onMouseUp={(e) => {
                e.stopPropagation();
                setInteracting(false);
            }}
            data-mounted={mounted}
            data-visible={isVisible}
            data-front={isFront}
            data-expanded={expanded}
        >
            {(expanded || isFront) && (
                <div className="w-full h-full p-3">
                    <AiTask item={item.aiTaskData} />
                </div>
            )}
        </div>
    );
};

const StackingComponent: React.FC<{ items: ConvertedAiTask[] }> = ({ items }) => {
    const [heights, setHeights] = useState<HeightT[]>([]);
    const [expanded, setExpanded] = useState(false);
    const [interacting, setInteracting] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
    const [position, setPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
    const startPositionRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

    // Limit visible items
    const visibleItems = items.slice(0, VISIBLE_ITEMS_AMOUNT);
    const remainingCount = Math.max(0, items.length - VISIBLE_ITEMS_AMOUNT);
    const hasMoreItems = remainingCount > 0;

    // Reset heights when items change significantly
    useEffect(() => {
        const currentTaskIds = new Set(items.map((item, index) => item.task_id || `item-${index}`));
        setHeights((prevHeights) =>
            prevHeights.filter((height) => currentTaskIds.has(height.taskId))
        );
    }, [items]);

    // Drag handlers
    const onMouseDownCapture = (e: React.MouseEvent<HTMLDivElement>) => {
        // Only start dragging with primary button and avoid interactive elements
        if (e.button !== 0) return;
        const target = e.target as HTMLElement;
        if (target.closest('button, a, input, textarea, [role="button"]')) {
            return;
        }
        e.preventDefault();
        setIsDragging(true);
        setInteracting(true);
        setExpanded(false);
        setDragStart({ x: e.clientX, y: e.clientY });
        startPositionRef.current = { ...position };
    };

    useEffect(() => {
        if (!isDragging) return;
        const handleMouseMove = (e: MouseEvent | PointerEvent) => {
            if (!dragStart) return;
            const dx = e.clientX - dragStart.x;
            const dy = e.clientY - dragStart.y;
            setPosition({ x: startPositionRef.current.x + dx, y: startPositionRef.current.y + dy });
        };
        const handleMouseUp = () => {
            setIsDragging(false);
            setInteracting(false);
            setDragStart(null);
        };
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        window.addEventListener('pointermove', handleMouseMove);
        window.addEventListener('pointerup', handleMouseUp);
        window.addEventListener('pointercancel', handleMouseUp);
        window.addEventListener('mouseleave', handleMouseUp);
        window.addEventListener('blur', handleMouseUp);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('pointermove', handleMouseMove);
            window.removeEventListener('pointerup', handleMouseUp);
            window.removeEventListener('pointercancel', handleMouseUp);
            window.removeEventListener('mouseleave', handleMouseUp);
            window.removeEventListener('blur', handleMouseUp);
        };
    }, [isDragging, dragStart]);

    return (
        <div
            className="fixed z-50"
            style={{ top: position.y, left: position.x, cursor: isDragging ? 'grabbing' : 'grab', userSelect: isDragging ? 'none' : undefined }}
        >
            {/* Inject the CSS for gap filling */}
            <style dangerouslySetInnerHTML={{ __html: stackItemStyles }} />

            <div
                className="relative pointer-events-auto"
                onMouseDownCapture={onMouseDownCapture}
                onMouseEnter={() => setExpanded(true)}
                onMouseLeave={() => {
                    if (!interacting) {
                        setExpanded(false);
                    }
                }}
                onMouseUpCapture={() => { setIsDragging(false); setInteracting(false); setDragStart(null); }}
            >
                {/** Precompute totals for consistent positioning */}
                {(() => null)()}
                {visibleItems.map((item, index) => {
                    const isVisible = index < VISIBLE_ITEMS_AMOUNT;
                    const isFront = index === 0;
                    const frontTaskId = visibleItems[0]?.task_id || 'item-0';
                    const frontHeight = heights.find((h) => h.taskId === frontTaskId)?.height || 0;

                    return (
                        <StackItem
                            key={item.task_id || `item-${index}`}
                            item={item}
                            index={index}
                            isVisible={isVisible}
                            isFront={isFront}
                            heights={heights}
                            setHeights={setHeights}
                            expanded={expanded}
                            setInteracting={setInteracting}
                            totalItems={visibleItems.length + (hasMoreItems ? 1 : 0)}
                            gap={GAP}
                            cornerOffset={CORNER_OFFSET}
                            frontHeight={frontHeight}
                        />
                    );
                })}

                {/* Overflow count card */}
                {hasMoreItems && (
                    <div
                        className={cn(
                            'absolute left-0 w-80 rounded-xl bg-card',
                            'transition-all duration-300 ease-out pointer-events-none',
                            'flex items-center justify-center text-center',
                            expanded ? 'opacity-100' : 'opacity-70',
                            expanded ? 'stack-item-expanded' : ''
                        )}
                        style={{
                            zIndex: 0,
                            // Place the overflow card just after the last visible item, but with less vertical gap
                            transform: expanded
                                ? (() => {
                                    const baseCornerX = 0;
                                    const baseCornerY = 0;
                                    // Only a small gap after the last visible item
                                    const offsetAfterVisible =
                                        heights
                                            .slice(0, VISIBLE_ITEMS_AMOUNT)
                                            .reduce((sum, h) => sum + h.height, 0) +
                                        VISIBLE_ITEMS_AMOUNT * GAP +
                                        2; // much smaller than GAP
                                    const x = CORNER_OFFSET + baseCornerX;
                                    const y = CORNER_OFFSET + baseCornerY + offsetAfterVisible;
                                    return `translate(${x}px, ${y}px)`;
                                })()
                                : `translate(${CORNER_OFFSET + (visibleItems.length) * STACK_OFFSET}px, ${CORNER_OFFSET + (visibleItems.length) * STACK_OFFSET}px)`,
                            transformOrigin: 'top left',
                            height: '45px',
                        } as React.CSSProperties}
                    >
                        <div className="text-xs text-textcolor leading-tight flex flex-col">
                            <span className='font-bold'> +{remainingCount} More tasks waiting </span>
                            <span> <span className='font-bold'>Accept</span> or <span className='font-medium'>dismiss</span> current tasks to see more</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StackingComponent;